// Module: useInstrumentTick
// Purpose: Return a liveTick-shaped object for a given resolved_instrument_id by
//          reading TradingDataContext.deploymentTicksRef. Re-renders when new ticks
//          arrive via the rAF-coalesced versions.deploymentTicks bump. Returns null
//          when no tick is cached yet, allowing callers to pass `undefined` to
//          downstream consumers that guard on missing tick data.

import { useContext, useMemo } from 'react';
import { TradingDataContext } from '../../contexts/TradingDataContext';

/**
 * Returns a liveTick-shaped object for the given resolved instrument id, or null
 * if the instrument id is absent or no tick has been received yet.
 *
 * Return shape (when a tick is cached):
 *   { ltp, volume, exchange_time, tick_type, change, changePct, direction,
 *     bid_prices, ask_prices, bid_qtys, ask_qtys, bid_orders, ask_orders,
 *     total_buy_qty, total_sell_qty }
 *
 * The depth fields (DL-5) are merge-retained upstream by TradingDataContext's
 * handleTick — this hook just surfaces whatever is currently cached, null when
 * no depth has ever arrived for this instrument.
 *
 * The hook is cheap: it reads a ref (no subscription overhead) and only causes a
 * re-render when the rAF-coalesced versions.deploymentTicks counter bumps —
 * the same mechanism useDeploymentLtp and useLastTickTime use.
 *
 * @param {string|null|undefined} instrumentId  resolved_instrument_id
 * @returns {{ ltp: number, volume: number|null, exchange_time: string|null,
 *             tick_type: string|null, change: number|null, changePct: number|null,
 *             direction: string, bid_prices: number[]|null, ask_prices: number[]|null,
 *             bid_qtys: number[]|null, ask_qtys: number[]|null, bid_orders: number[]|null,
 *             ask_orders: number[]|null, total_buy_qty: number|null,
 *             total_sell_qty: number|null } | null}
 */
export function useInstrumentTick(instrumentId) {
  const ctx = useContext(TradingDataContext);
  if (!ctx) throw new Error('useInstrumentTick must be used within a TradingDataProvider');

  const { deploymentTicksRef, versions } = ctx;

  return useMemo(() => {
    if (!instrumentId) return null;

    const entry = deploymentTicksRef.current.get(instrumentId);
    if (!entry || entry.ltp == null) return null;

    return {
      ltp: entry.ltp,
      volume: entry.volume ?? null,
      exchange_time: entry.exchange_time ?? null,
      tick_type: entry.tickType ?? null,
      change: entry.change ?? null,
      changePct: entry.changePct ?? null,
      direction: entry.direction ?? 'neutral',
      bid_prices: entry.bid_prices ?? null,
      ask_prices: entry.ask_prices ?? null,
      bid_qtys: entry.bid_qtys ?? null,
      ask_qtys: entry.ask_qtys ?? null,
      bid_orders: entry.bid_orders ?? null,
      ask_orders: entry.ask_orders ?? null,
      total_buy_qty: entry.total_buy_qty ?? null,
      total_sell_qty: entry.total_sell_qty ?? null,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  //! versions.deploymentTicks is the rAF-coalesced counter that bumps on every
  //! paint frame with new ticks. instrumentId in deps handles instrument-switch.
  }, [instrumentId, versions.deploymentTicks]);
}
